import { CheckCircle, UploadCloud, XCircle, Info, X, Copy, Loader, Zap } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react'; 

// --- UI PRIMITIVES ---

export const AppCard = ({ children, className = '', ...props }) => (
    <div className={`bg-white/5 dark:bg-black/20 backdrop-blur-md border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-lg transition-colors duration-300 ${className}`} {...props}>
        {children}
    </div>
);

export const SectionTitle = ({ icon: Icon, children, ...props }) => (
    <h2 className="text-3xl md:text-4xl text-zinc-900 dark:text-white tracking-tight mb-6 flex items-center gap-3 font-poppins-light-italic" {...props}>
        {Icon && <Icon className="w-8 h-8 text-purple-600 dark:text-purple-400" />}
        <span>{children}</span>
    </h2>
);

export const AppButton = ({ children, onClick, variant = 'primary', size = 'md', className = '', disabled = false, icon: Icon, type = "button", ...props }) => {
    const baseStyle = "inline-flex items-center justify-center font-semibold rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-100 dark:focus:ring-offset-zinc-950 transition-all duration-200 ease-in-out";
    const sizeStyles = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-base" };
    const variantStyles = {
        primary: "bg-purple-600 hover:bg-purple-500 text-white focus:ring-purple-500 border border-transparent disabled:bg-purple-400",
        secondary: "bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-200 focus:ring-zinc-500 border border-zinc-300 dark:border-zinc-700",
        danger: "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 border border-transparent",
        ghost: "bg-transparent hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300 focus:ring-zinc-600"
    };
    
    return (
        <button type={type} onClick={onClick} disabled={disabled} className={`${baseStyle} ${sizeStyles[size]} ${variantStyles[variant]} ${disabled ? "opacity-60 cursor-not-allowed" : ""} ${className}`} {...props}>
            {Icon && <Icon size={size === 'sm' ? 16 : 18} className={children ? "mr-2" : ""} />}
            {children}
        </button>
    );
};

export const AppInput = ({ ...props }) => <input {...props} className="w-full p-3 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors" />;
export const AppTextarea = ({ ...props }) => <textarea {...props} className="w-full p-3 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors resize-y" />;
export const AppSelect = ({ children, ...props }) => <select {...props} className="w-full p-3 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"> {children} </select>;

export const BlackboxLoaderIcon = ({ className, variant = 'default' }) => (
    <>
      <style>{`
        @keyframes pulseColor {
          0%, 100% { stroke: #a855f7; } 
          50% { stroke: #38bdf8; }
        }
        @keyframes whitePulseGlow { 
           0%, 100% { stroke: #ffffff; text-shadow: 0 0 5px #ffffff, 0 0 10px #a855f7; }
            50% { stroke: #a78bfa; text-shadow: 0 0 10px #a78bfa, 0 0 20px #a78bfa; }
        }
        .pulse-rect { animation: pulseColor 2s ease-in-out infinite; }
        .pulse-line { animation: pulseColor 2s ease-in-out infinite; animation-delay: -1s; }
        .white-pulse-glow-rect { animation: whitePulseGlow 2s ease-in-out infinite; }
        .white-pulse-glow-line { animation: whitePulseGlow 2s ease-in-out infinite; animation-delay: -1s; }
      `}</style>
      <svg className={className} viewBox="0 0 24 24" fill="none" strokeWidth="2">
          {variant === 'white-pulse' ? (
              <>
                  <rect className="white-pulse-glow-rect" x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" />
                  <line className="white-pulse-glow-line" x1="3" y1="12" x2="21" y2="12" stroke="currentColor" />
              </>
          ) : (
              <>
                  <rect className="pulse-rect" x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" />
                  <line className="pulse-line" x1="3" y1="12" x2="21" y2="12" stroke="currentColor" />
              </>
          )}
      </svg>
    </>
);

export const BlackboxIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="4" y="4" width="16" height="16" rx="3" />
        <line x1="3" y1="12" x2="21" y2="12" />
    </svg>
);

export const InfoCard = ({ icon: Icon, title, children, className }) => (
    <AppCard className={`p-6 ${className}`}>
        <h3 className="font-semibold text-lg text-zinc-900 dark:text-white mb-3 flex items-center gap-2 font-poppins">
            <Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            {title}
        </h3>
        {children}
    </AppCard>
);

export const StatCard = ({ title, value, icon: Icon, color, className = '' }) => (
    <AppCard className={`p-4 ${className}`}>
      <div className="flex items-center">
        <div className={`p-3 rounded-lg mr-4 ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-zinc-400">{title}</p>
          <p className="text-2xl font-bold text-white font-inter">{value}</p>
        </div>
      </div>
    </AppCard>
);

// --- OVERHAULED: PREMIUM NOTIFICATION TOAST ---
export const Notification = ({ message, type, onDismiss }) => {
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                onDismiss();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message, onDismiss]);

    if (!message) return null;

    const styles = {
        success: {
            bg: "bg-emerald-500/10",
            border: "border-emerald-500/50",
            icon: <CheckCircle className="text-emerald-400" size={20} />,
            text: "text-emerald-50",
            glow: "shadow-emerald-500/20"
        },
        error: {
            bg: "bg-red-500/10",
            border: "border-red-500/50",
            icon: <XCircle className="text-red-400" size={20} />,
            text: "text-red-50",
            glow: "shadow-red-500/20"
        },
        info: {
            bg: "bg-blue-500/10",
            border: "border-blue-500/50",
            icon: <Info className="text-blue-400" size={20} />,
            text: "text-blue-50",
            glow: "shadow-blue-500/20"
        },
        credit: {
            bg: "bg-purple-500/10",
            border: "border-purple-500/50",
            icon: <Zap className="text-purple-400" size={20} />,
            text: "text-purple-50",
            glow: "shadow-purple-500/20"
        }
    };

    const currentStyle = styles[type] || styles.info;

    return (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] w-full max-w-md px-4 animate-in fade-in slide-in-from-top-4 duration-300 pointer-events-none">
            <div className={`${currentStyle.bg} ${currentStyle.border} ${currentStyle.glow} backdrop-blur-md border rounded-xl p-4 shadow-2xl flex items-center gap-4 pointer-events-auto`}>
                <div className="shrink-0">
                    {currentStyle.icon}
                </div>
                <div className="flex-grow">
                    <p className={`${currentStyle.text} text-sm font-medium leading-relaxed`}>
                        {message}
                    </p>
                </div>
                <button 
                    onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                    className="shrink-0 text-zinc-500 hover:text-white transition-colors p-1"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
};

export const AiContentModal = ({ isOpen, onClose, content, isLoading, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <AppCard className="p-8 max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold font-poppins">{title}</h3>
          <AppButton onClick={onClose} variant="ghost" size="sm" icon={X}>Close</AppButton>
        </div>
        <div className="flex-grow overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <BlackboxLoaderIcon className="w-12 h-12" />
              <p className="ml-4 text-zinc-400">Generating content...</p>
            </div>
          ) : (
            <div className="prose prose-sm prose-invert max-w-none">
              <pre className="whitespace-pre-wrap text-zinc-300 bg-zinc-900 p-4 rounded-lg">{content}</pre>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-6 border-t border-zinc-800 mt-6">
          <AppButton onClick={() => navigator.clipboard.writeText(content)} variant="secondary" icon={Copy}>Copy</AppButton>
          <AppButton onClick={onClose}>Close</AppButton>
        </div>
      </AppCard>
    </div>
  );
};

export const AppModal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-zinc-900 border border-zinc-700 rounded-xl w-full shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200 ${sizeClasses[size] || sizeClasses.md}`} onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-zinc-800">
          <h3 className="text-xl font-bold text-white font-poppins">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
            {children}
        </div>
      </div>
    </div>
  );
};

export const ImageUploader = ({ onFileSelect, uploadedFile, label, setNotification, className = "" }) => {
    const [isDragging, setIsDragging] = useState(false);
    const dropRef = useRef(null);

    const handleFileChange = (files) => {
        if (files && files[0] && files[0].type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => onFileSelect(e.target.result); // Pass base64 string
            reader.readAsDataURL(files[0]);
        } else {
            if (setNotification) setNotification({ type: 'error', message: 'Please upload a valid image file.' });
        }
    };

    const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDragIn = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileChange(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    };

    useEffect(() => {
        let div = dropRef.current;
        if (div) {
            div.addEventListener('dragenter', handleDragIn);
            div.addEventListener('dragleave', handleDragOut);
            div.addEventListener('dragover', handleDrag);
            div.addEventListener('drop', handleDrop);
        }
        return () => {
            if (div) {
                div.removeEventListener('dragenter', handleDragIn);
                div.removeEventListener('dragleave', handleDragOut);
                div.removeEventListener('dragover', handleDrag);
                div.removeEventListener('drop', handleDrop);
            }
        };
    }, []);

    return (
        <div className={`w-full ${className}`}>
            {label && <h3 className="text-sm font-semibold text-zinc-300 mb-2">{label}</h3>}
            <div ref={dropRef} className={`relative w-full h-64 border-2 border-dashed ${isDragging ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-700 bg-zinc-800/30'} rounded-xl flex justify-center items-center text-center p-4 transition-all hover:border-purple-500/50 group`}>
                {uploadedFile ? (
                    <div className="relative w-full h-full">
                        <img src={uploadedFile} alt="Uploaded poster" className="w-full h-full object-contain rounded-lg\"/>
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                            <p className="text-white font-medium">Click or Drop to Replace</p>
                        </div>
                    </div>
                ) : (
                    <div className="text-zinc-500 group-hover:text-purple-400 transition-colors">
                        <UploadCloud className="w-10 h-10 mx-auto mb-2" />
                        <p className="text-sm">Drag & drop or click to upload</p>
                    </div>
                )}
                <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileChange(e.target.files)} accept="image/*" />
            </div>
        </div>
    );
};